import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Star, Crown, ArrowRight, Briefcase, Link2, Check } from 'lucide-react';
import { ImageWithFallback } from './figma/ImageWithFallback';
import type { Job } from '../types';
import { cleanText, normalizeTitle } from '../utils/sanitize';

interface JobCardProps {
  job: Job;
  compact?: boolean;
}

export default function JobCard({ job }: JobCardProps) {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);

  const { id, title, company, description } = job;
  // Determina o tipo de vaga com prioridade correta
  // 1. Se job_type vem do backend, usa ele
  // 2. Caso contrário, verifica is_promoted (premium) e is_featured (destaque)
  // IMPORTANTE: is_promoted = premium (coroa roxa), is_featured = destaque (estrela laranja)
  const jobType: 'basica' | 'destaque' | 'premium' =
    (job as any).job_type === 'normal' ? 'basica' :
    (job as any).job_type ||
    (job.is_promoted ? 'premium' : job.is_featured ? 'destaque' : 'basica');

  // Tenta obter a logo da empresa de diferentes fontes possíveis
  const jobData = job as Record<string, unknown>;
  // Suporta múltiplas chaves possíveis vindas da API
  const companyLogo =
    (company as any)?.logo ||
    (company as any)?.logo_url ||
    (jobData.company_logo as string) ||
    null;

  const handleClick = () => {
    navigate(`/vaga/${id}`);
  };

  const handleCopyLink = (e: React.MouseEvent) => {
    e.stopPropagation();
    const url = `${window.location.origin}/vaga/${id}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  // Renderiza ícone de destaque
  const renderHighlightIcon = () => {
    // Premium (criado como premium): cor roxa
    if (jobType === 'premium') {
      return (
        <div className="absolute -top-2 -right-2 rounded-full p-1.5 shadow-md bg-purple-600">
          <Crown className="size-4 text-white" />
        </div>
      );
    }
    // Destaque ou vaga básica impulsionada: cor laranja
    if (jobType === 'destaque' || job.is_featured) {
      return (
        <div className="absolute -top-2 -right-2 bg-amber-500 rounded-full p-1.5 shadow-md">
          <Star className="size-4 text-white" />
        </div>
      );
    }
    // Vaga básica: badge azul
    return (
      <div className="absolute -top-2 -right-2 bg-blue-500 rounded-full p-1.5 shadow-md">
        <Briefcase className="size-4 text-white" />
      </div>
    );
  };

  // Limita a descrição
  const truncateDescription = (text: string, maxLength: number = 120) => {
    if (!text) return '';
    // Limpa HTML primeiro
    const cleaned = cleanText(text);
    if (cleaned.length <= maxLength) return cleaned;
    return cleaned.substring(0, maxLength).trim() + '...';
  };

  // Define estilo do card baseado no tipo
  const getCardStyle = () => {
    // Premium (criado como premium): borda roxa
    if (jobType === 'premium') return 'bg-white border-purple-200 ring-1 ring-purple-100';
    // Destaque ou vaga básica impulsionada: borda laranja
    if (jobType === 'destaque' || job.is_featured) return 'bg-white border-amber-200 ring-1 ring-amber-100';
    return 'bg-white border-gray-200';
  };

  return (
    <div
      className={`border rounded-lg p-5 hover:shadow-lg transition-all cursor-pointer h-full flex flex-col ${getCardStyle()}`}
      onClick={handleClick}
    >
      {/* Header: Logo + Título (no lugar da data) */}
      <div className="flex items-start gap-4 mb-4 pb-4 border-b border-gray-100">
        <div className="relative flex-shrink-0">
          {(job as any).hide_company_name ? (
            <div className="size-12 bg-gray-200 rounded-lg flex items-center justify-center">
              <span className="text-gray-500 font-bold text-lg">?</span>
            </div>
          ) : companyLogo ? (
            <ImageWithFallback
              src={companyLogo}
              alt={company?.company_name || 'Empresa'}
              className="size-12 rounded-lg object-contain"
            />
          ) : (
            <div className="size-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <span className="text-blue-600 font-bold text-lg">
                {company?.company_name?.charAt(0) || 'E'}
              </span>
            </div>
          )}
          {renderHighlightIcon()}
        </div>
        <h3 className="font-bold text-gray-900 text-base sm:text-lg leading-snug line-clamp-2 flex-1">
          {normalizeTitle(title)}
        </h3>
      </div>

      {/* Descrição */}
      <p className="text-gray-500 text-sm mb-4 line-clamp-4 flex-1">
        {truncateDescription(description, 150)}
      </p>

      {/* Saiba mais + Copiar link */}
      <div className="pt-4 border-t border-gray-100 mt-auto">
        <span className="flex items-center justify-between w-full text-white hover:text-gray-900 transition-colors group">
          <span className="bg-[#87ca1c] py-2 px-2 rounded text-sm font-medium">Saiba mais</span>
          <div className="flex items-center gap-2">
            <button
              onClick={handleCopyLink}
              className="p-1.5 rounded-md hover:bg-gray-100 transition-colors text-gray-500 hover:text-gray-700"
              title="Copiar link da vaga"
            >
              {copied ? <Check className="size-4 text-green-600" /> : <Link2 className="size-4" />}
            </button>
            <ArrowRight className="size-4 group-hover:translate-x-1 text-gray-900 transition-transform" />
          </div>
        </span>
      </div>
    </div>
  );
}
